class NvcTwoStylePairing {
  constructor(root) {
    this.root = root;
    this.results = root.querySelector('[data-two-style-results]');
    this.sourceType = root.dataset.sourceType || '';
    this.sourcePrice = Number(root.dataset.sourcePrice || 0);
    this.sourceOptions = this.parseOptions(root.dataset.sourceOptions);
    this.productData = this.getProductData();
    this.handleVariantChange = (event) => this.updateSource(event.detail?.variant);
    this.handleNativeVariantChange = (event) => {
      if (!event.target.closest('variant-picker-v2, variant-selects, variant-radios, .variations')) return;
      window.setTimeout(() => this.syncFromProductControls(), 0);
      window.setTimeout(() => this.syncFromProductControls(), 80);
    };
    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('product:variant-change', this.handleVariantChange);
    document.addEventListener('change', this.handleNativeVariantChange);
    document.addEventListener('click', this.handleNativeVariantChange);
    this.load();
  }

  getProductData() {
    const productId = String(this.root.dataset.productId || '');
    for (const script of document.querySelectorAll('[data-vp2-product]')) {
      try {
        const data = JSON.parse(script.textContent);
        if (String(data.id) === productId) return data;
      } catch (error) {
        continue;
      }
    }
    return null;
  }

  syncFromProductControls() {
    if (!this.productData?.variants) return;
    const picker = document.querySelector(`variant-picker-v2[data-product-id="${this.root.dataset.productId}"]`);
    const variantId = picker?.querySelector('[data-vp2-variant-id]')?.value;
    const variant = this.productData.variants.find((item) => String(item.id) === String(variantId));
    if (variant) this.updateSource(variant);
  }

  parseOptions(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    try {
      return JSON.parse(value).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  modelFrom(options) {
    for (const value of this.parseOptions(options)) {
      const match = String(value).match(/iPhone\s*(?:Air|SE(?:\s*\d+)?|\d{1,2}(?:\s*(?:Pro Max|Pro|Plus|Mini|Air|e))?)/i);
      if (match) return match[0].replace(/\s+/g, ' ').trim();
    }
    return '';
  }

  updateSource(variant) {
    if (!variant) return;
    this.sourcePrice = Number(variant.price || this.sourcePrice);
    this.sourceOptions = Array.isArray(variant.options)
      ? variant.options.filter(Boolean)
      : [variant.option1, variant.option2, variant.option3].filter(Boolean);
    this.syncPairings();
  }

  async load() {
    const url = this.root.dataset.url;
    if (!url) {
      this.bindCards();
      this.syncPairings({updateImage: false});
      return;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('recommendations');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const replacement = doc.querySelector('[data-two-style-results]');
      if (replacement) {
        this.results.replaceWith(replacement);
        this.results = this.root.querySelector('[data-two-style-results]');
        this.bindCards();
        this.syncPairings({updateImage: false});
      }
    } catch (error) {
      this.results.innerHTML = '';
    }
  }

  bindCards() {
    this.root.querySelectorAll('[data-two-style-card]').forEach((card) => {
      const form = card.querySelector('[data-two-style-add]');
      if (!form || form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', (event) => this.add(event, form));
      const variantScript = card.querySelector('[data-two-style-variants]');
      if (!variantScript) {
        this.updateSaving(card);
        return;
      }
      try {
        card.nvcVariants = JSON.parse(variantScript.textContent);
      } catch (error) {
        card.nvcVariants = [];
      }
      card.querySelectorAll('[data-two-style-option-value]').forEach((button) => {
        button.addEventListener('click', () => this.selectOptionValue(card, button));
      });
      card.querySelectorAll('[data-two-style-model-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const fieldset = button.closest('[data-two-style-option]');
          const open = !fieldset?.classList.contains('is-open');
          this.root.querySelectorAll('[data-two-style-option].is-open').forEach((group) => {
            group.classList.remove('is-open');
            group.querySelector('[data-two-style-model-change]')?.setAttribute('aria-expanded', 'false');
          });
          fieldset?.classList.toggle('is-open', open);
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) {
            window.requestAnimationFrame(() => {
              fieldset?.querySelector('[data-two-style-option-value].is-selected')?.scrollIntoView({block: 'nearest', inline: 'nearest'});
            });
          }
        });
        button.closest('[data-two-style-option]')?.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          const fieldset = button.closest('[data-two-style-option]');
          fieldset?.classList.remove('is-open');
          button.setAttribute('aria-expanded', 'false');
          button.focus();
        });
      });
      this.identifyModelOption(card);
      card.querySelectorAll('.nvc-two-style-option.is-visual').forEach((fieldset) => this.setupVisualScroller(fieldset));
      const input = form.querySelector('[data-two-style-variant-id]');
      const initial = card.nvcVariants.find((variant) => String(variant.id) === String(input?.value))
        || card.nvcVariants.find((variant) => variant.available);
      if (initial) this.setCardVariant(card, initial, {updateImage: false});
    });
  }

  identifyModelOption(card) {
    card.querySelectorAll('[data-two-style-option]').forEach((fieldset) => {
      const values = Array.from(fieldset.querySelectorAll('[data-two-style-option-value]'));
      const optionName = this.normalize(fieldset.dataset.optionName);
      const isDevice = optionName.includes('機種') || optionName.includes('対応') || optionName.includes('device') || optionName.includes('model');
      const isTextOnly = fieldset.dataset.textOnly === 'true';
      if (isTextOnly) {
        fieldset.classList.add('is-model', 'is-text');
        values.forEach((button) => {
          button.classList.remove('has-thumbnail');
          button.querySelector('img')?.remove();
        });
      } else if (isDevice || values.some((button) => Boolean(this.modelFrom([button.dataset.value])))) {
        fieldset.classList.add('is-model');
      } else {
        fieldset.classList.add('is-visual');
      }
      const isSet = values.some((button) => /(セット|単品|ストラップ付き|チャーム|リング付き|ケース\+)/.test(button.dataset.value || ''));
      if (isSet) {
        const legend = fieldset.querySelector('legend');
        if (legend) legend.textContent = card.dataset.setLabel || legend.textContent;
        const summaryLabel = fieldset.querySelector('[data-two-style-option-label]');
        if (summaryLabel) summaryLabel.textContent = card.dataset.setLabel || summaryLabel.textContent;
      }
    });
  }

  setupVisualScroller(fieldset) {
    const rail = fieldset.querySelector('[data-two-style-option-values]');
    const nav = fieldset.querySelector('[data-two-style-option-nav]');
    const previous = fieldset.querySelector('[data-two-style-scroll-prev]');
    const next = fieldset.querySelector('[data-two-style-scroll-next]');
    if (!rail || !nav || !previous || !next) return;
    const update = () => {
      const items = Array.from(rail.querySelectorAll('[data-two-style-option-value]'));
      const railRect = rail.getBoundingClientRect();
      const firstRect = items[0]?.getBoundingClientRect();
      const lastRect = items[items.length - 1]?.getBoundingClientRect();
      const overflow = rail.scrollWidth > rail.clientWidth + 2;
      nav.hidden = !overflow;
      previous.disabled = !overflow || !firstRect || firstRect.left >= railRect.left - 1;
      next.disabled = !overflow || !lastRect || lastRect.right <= railRect.right + 1;
    };
    const move = (direction) => {
      const items = Array.from(rail.querySelectorAll('[data-two-style-option-value]'));
      const railRect = rail.getBoundingClientRect();
      const target = direction > 0
        ? items.find((item) => item.getBoundingClientRect().right > railRect.right + 2)
        : items.reverse().find((item) => item.getBoundingClientRect().left < railRect.left - 2);
      if (!target) return;
      rail.scrollLeft = Math.max(0, target.offsetLeft - 6);
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    rail.addEventListener('scroll', update, {passive: true});

    let drag = null;
    rail.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      drag = {x: event.clientX, scroll: rail.scrollLeft, moved: false};
      rail.classList.add('is-dragging');
    });
    window.addEventListener('mousemove', (event) => {
      if (!drag) return;
      const delta = event.clientX - drag.x;
      if (Math.abs(delta) > 4) drag.moved = true;
      rail.scrollLeft = drag.scroll - delta;
      if (drag.moved) event.preventDefault();
    });
    const finishDrag = () => {
      if (!drag) return;
      if (drag.moved) rail.dataset.justDragged = 'true';
      drag = null;
      rail.classList.remove('is-dragging');
      window.setTimeout(() => delete rail.dataset.justDragged, 0);
    };
    window.addEventListener('mouseup', finishDrag);
    window.addEventListener('blur', finishDrag);
    rail.addEventListener('click', (event) => {
      if (rail.dataset.justDragged === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(rail);
    window.requestAnimationFrame(update);
  }

  selectOptionValue(card, button) {
    if (button.disabled || !card.nvcVariants?.length) return;
    const fieldset = button.closest('[data-two-style-option]');
    const optionIndex = Number(fieldset?.dataset.optionIndex || 0);
    const nextOptions = [...(card.nvcSelectedOptions || [])];
    nextOptions[optionIndex] = button.dataset.value;
    let variant = card.nvcVariants.find((item) => item.available && this.optionsEqual(item.options, nextOptions));
    if (!variant) variant = card.nvcVariants.find((item) => item.available && item.options?.[optionIndex] === button.dataset.value);
    if (!variant) return;
    this.setCardVariant(card, variant);
    if (fieldset) {
      fieldset.classList.remove('is-open');
      fieldset.querySelector('[data-two-style-model-change]')?.setAttribute('aria-expanded', 'false');
    }
  }

  setCardVariant(card, variant, {updateImage = true} = {}) {
    const input = card.querySelector('[data-two-style-variant-id]');
    if (!input || !variant) return;
    card.nvcSelectedOptions = [...variant.options];
    input.value = variant.id;
    input.dataset.selectedVariantId = variant.id;
    input.dataset.price = variant.price;
    input.dataset.options = JSON.stringify(variant.options);
    const image = card.querySelector('.nvc-two-style-card__media img');
    if (updateImage && image && variant.featured_image?.src) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = variant.featured_image.src;
    }
    card.querySelectorAll('[data-two-style-option]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex || 0);
      const selectedValue = variant.options[optionIndex];
      fieldset.querySelectorAll('[data-two-style-option-value]').forEach((button) => {
        const selected = button.dataset.value === selectedValue;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      const modelValue = fieldset.querySelector('[data-two-style-model-value]');
      if (modelValue) {
        modelValue.textContent = selectedValue;
        modelValue.title = selectedValue;
      }
    });
    this.updateOptionAvailability(card);
    const price = card.querySelector('[data-two-style-price]');
    if (price) price.textContent = this.formatMoney(Number(variant.price));
    this.updateSaving(card);
  }

  updateOptionAvailability(card) {
    const selectedOptions = card.nvcSelectedOptions || [];
    card.querySelectorAll('[data-two-style-option]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex || 0);
      fieldset.querySelectorAll('[data-two-style-option-value]').forEach((button) => {
        const value = button.dataset.value;
        const available = card.nvcVariants.some((variant) => variant.available
          && variant.options?.[optionIndex] === value
          && variant.options.every((optionValue, index) => index === optionIndex || optionValue === selectedOptions[index]));
        const isSelected = value === selectedOptions[optionIndex];
        button.disabled = !available && !isSelected;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
      });
    });
  }

  syncPairings({updateImage = true} = {}) {
    const model = this.modelFrom(this.sourceOptions);
    const modelWrap = this.root.querySelector('[data-two-style-model-wrap]');
    const modelText = this.root.querySelector('[data-two-style-model]');
    if (modelWrap && modelText) {
      modelWrap.hidden = !model;
      modelText.textContent = model;
    }
    this.root.querySelectorAll('[data-two-style-card]').forEach((card) => {
      const sameType = this.normalize(card.dataset.productType) === this.normalize(this.sourceType);
      const variants = card.nvcVariants || [];
      let selectedVariant = variants.find((variant) => variant.available);
      if (variants.length && sameType && model) {
        const currentOptions = card.nvcSelectedOptions || [];
        const modelIndex = this.findModelOptionIndex(variants);
        if (modelIndex >= 0) {
          selectedVariant = variants.find((variant) => variant.available
            && this.normalize(this.modelFrom([variant.options[modelIndex]])) === this.normalize(model)
            && variant.options.every((value, index) => index === modelIndex || !currentOptions[index] || value === currentOptions[index]))
            || variants.find((variant) => variant.available && this.normalize(this.modelFrom([variant.options[modelIndex]])) === this.normalize(model))
            || selectedVariant;
        }
      }
      if (selectedVariant) this.setCardVariant(card, selectedVariant, {updateImage});
      const compatibility = card.querySelector('[data-two-style-compatibility]');
      if (compatibility) compatibility.textContent = '';
      this.updateSaving(card);
    });
  }

  findModelOptionIndex(variants) {
    const sample = variants.find((variant) => variant.options?.length);
    if (!sample) return -1;
    return sample.options.findIndex((value, index) => variants.some((variant) => Boolean(this.modelFrom([variant.options?.[index]]))));
  }

  optionsEqual(left = [], right = []) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  normalize(value) {
    return String(value || '').replace(/\s+/g, '').toLowerCase();
  }

  updateSaving(card) {
    if (!card || !this.sourcePrice) return;
    const input = card.querySelector('[data-two-style-variant-id]');
    const candidatePrice = Number(input?.dataset.price || card.dataset.basePrice || 0);
    const amount = card.querySelector('[data-two-style-saving-amount]');
    if (!candidatePrice || !amount) return;
    amount.textContent = `${this.formatMoney(Math.round(Math.min(this.sourcePrice, candidatePrice) / 2))} OFF`;
  }

  formatMoney(cents) {
    const currency = this.root.dataset.currency || 'JPY';
    return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', {
      style: 'currency', currency, maximumFractionDigits: currency === 'JPY' ? 0 : 2
    }).format(cents / 100);
  }

  async add(event, form) {
    event.preventDefault();
    if (form.__nvcSubmitting) return;
    const button = form.querySelector('button[type="submit"]');
    const buttonText = button.querySelector('[data-two-style-button-text]');
    const originalText = buttonText?.textContent || '';
    const status = form.querySelector('[data-two-style-status]');
    const variantInput = form.querySelector('[data-two-style-variant-id]');
    const id = variantInput?.dataset.selectedVariantId || variantInput?.value;
    if (!id) {
      status.textContent = window.nvcTwoStyleStrings.chooseVariant;
      return;
    }
    form.__nvcSubmitting = true;
    button.disabled = true;
    status.textContent = '';
    if (buttonText) buttonText.textContent = window.nvcTwoStyleStrings.adding;
    try {
      const response = await window.nvcCartRequest(window.theme.routes.cart_add_url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items: [{id: Number(id), quantity: 1}]})
      }, {open:true});
      const card = form.closest('[data-two-style-card]');
      const eventDetail = {
        source_product_id: this.root.dataset.productId || '',
        paired_product_id: card?.dataset.productId || '',
        paired_product_handle: card?.dataset.productHandle || '',
        paired_product_title: card?.dataset.productTitle || '',
        variant_id: String(id),
        ranking_version: this.root.dataset.rankingVersion || ''
      };
      document.dispatchEvent(new CustomEvent('nvc:two-style:add', {bubbles: true, detail: eventDetail}));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: 'two_style_pairing_add', ...eventDetail});
      status.textContent = window.nvcTwoStyleStrings.added;
      if (buttonText) buttonText.textContent = `✓ ${window.nvcTwoStyleStrings.added}`;
      dispatchCustomEvent('cart:item-added', {product:response.items?.[0] || response});
    } catch (error) {
      status.textContent = error.userMessage || window.theme.variantStrings.addError;
      if (buttonText) buttonText.textContent = originalText;
    } finally {
      form.__nvcSubmitting = false;
      button.disabled = false;
      window.setTimeout(() => {
        if (buttonText) buttonText.textContent = originalText;
      }, 2200);
    }
  }
}

const initializeNvcTwoStyle = (scope = document) => {
  scope.querySelectorAll('[data-two-style-pairing]').forEach((root) => {
    if (!root.dataset.initialized) {
      root.dataset.initialized = 'true';
      new NvcTwoStylePairing(root);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => initializeNvcTwoStyle());
document.addEventListener('shopify:section:load', (event) => initializeNvcTwoStyle(event.target));
